// Tracks online/offline state and drains the offline sale queue
// when the connection is restored. Mount this once in Layout.
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';
import { getQueue, dequeue, markFailed } from '../utils/offlineQueue';

export default function useOfflineSync() {
  const [isOnline,   setIsOnline]   = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(() => getQueue().filter(x => !x.failed).length);
  const [syncing,    setSyncing]    = useState(false);
  const syncingRef = useRef(false);

  const refreshCount = useCallback(() => {
    setQueueCount(getQueue().filter(x => !x.failed).length);
  }, []);

  const syncQueue = useCallback(async () => {
    if (syncingRef.current) return;
    const pending = getQueue().filter(x => !x.failed);
    if (!pending.length) return;

    syncingRef.current = true;
    setSyncing(true);

    for (const sale of pending) {
      if (!navigator.onLine) break; // connection dropped mid-sync — stop

      try {
        // 1. Create document
        const doc = await api.post('/api/documents', {
          type: 200,
          account_id: sale.account_id || null,
          date: sale.date || new Date().toISOString().slice(0, 10),
          currency: 'USD',
          notes: sale.notes || '',
          source: sale.source || 'pos',
          ...(sale.delivery ? { delivery: sale.delivery } : {}),
          ...(sale.discount_amount ? { discount_amount: sale.discount_amount } : {}),
        }).then(r => r.data?.data ?? r.data);

        const docId = doc?._id ?? doc?.id;
        if (!docId) { markFailed(sale.id, 'No document ID returned'); continue; }

        // 2. Add items (bulk — one round trip)
        if ((sale.items || []).length > 0) {
          await api.post(`/api/documents/${docId}/items/bulk`, { items: sale.items });
        }

        // 3. Confirm
        await api.post(`/api/documents/${docId}/confirm`, {});

        // 4. Payment — recorded in the currency actually tendered (USD or secondary)
        if (sale.paidUSD > 0 && sale.methodId) {
          const cur = sale.pay_currency || 'USD';
          const amt = cur === 'USD' ? sale.paidUSD : (sale.pay_amount || sale.paidUSD);
          await api.post(`/api/documents/${docId}/payments`, {
            payment_method_id: sale.methodId,
            amount: amt,
            currency: cur,
          });
        }

        dequeue(sale.id);
      } catch (err) {
        const isNetErr = !navigator.onLine
          || err?.code === 'ERR_NETWORK'
          || err?.message === 'Network Error';

        if (isNetErr) {
          break; // stop processing — network went down again
        } else {
          // Server rejected this specific sale (bad product ID, etc.)
          // Mark as failed so we don't retry in a loop; user must clear manually
          markFailed(sale.id, err?.response?.data?.error || err?.message || 'Server error');
        }
      }
    }

    setSyncing(false);
    syncingRef.current = false;
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    // Drain queue on reconnect
    const onOnline = () => { setIsOnline(true); syncQueue(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [syncQueue]);

  useEffect(() => {
    // Sync count whenever another component enqueues/dequeues
    window.addEventListener('mmm_queue_updated', refreshCount);
    return () => window.removeEventListener('mmm_queue_updated', refreshCount);
  }, [refreshCount]);

  // Attempt sync on mount (in case app was opened while offline then came back)
  useEffect(() => {
    if (navigator.onLine && getQueue().filter(x => !x.failed).length > 0) {
      syncQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isOnline, queueCount, syncing, syncQueue, refreshCount };
}
